import { useEffect, useState } from 'react';
import { Keyboard } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SkipLinkProps {
  targetId: string;
  className?: string;
}

/**
 * Skip link for keyboard users to jump to main content
 */
export function SkipLink({ targetId, className }: SkipLinkProps) {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      target.setAttribute('tabindex', '-1');
      target.focus();
      target.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <a
      href={`#${targetId}`}
      onClick={handleClick}
      className={cn(
        'sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[9999]',
        'bg-primary text-primary-foreground px-4 py-2 rounded-lg shadow-lg',
        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
        'font-medium text-sm',
        className
      )}
    >
      Skip to main content
    </a>
  );
}

/**
 * Visual focus indicator that follows keyboard focus
 */
export function FocusRing() {
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    let isKeyboard = false;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        isKeyboard = true;
      }
    };

    const handleMouseDown = () => {
      isKeyboard = false;
      setIsVisible(false);
    };

    const handleFocus = (e: FocusEvent) => {
      if (!isKeyboard) return;

      const target = e.target as HTMLElement;
      if (!target || target === document.body) {
        setIsVisible(false);
        return;
      }

      const rect = target.getBoundingClientRect();
      setPosition({
        top: rect.top + window.scrollY - 2,
        left: rect.left + window.scrollX - 2,
        width: rect.width + 4,
        height: rect.height + 4,
      });
      setIsVisible(true);
    };

    const handleBlur = () => {
      setIsVisible(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('focusin', handleFocus);
    document.addEventListener('focusout', handleBlur);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleBlur);
    };
  }, []);

  if (!isVisible || !position) return null;

  return (
    <div
      className="pointer-events-none fixed z-[9999] rounded-lg border-2 border-primary ring-2 ring-primary/30 transition-all duration-150"
      style={{
        top: position.top,
        left: position.left,
        width: position.width,
        height: position.height,
      }}
    />
  );
}

/**
 * Keyboard navigation hint tooltip
 */
interface KeyboardHintProps {
  keys: string[];
  description: string;
  className?: string;
}

export function KeyboardHint({ keys, description, className }: KeyboardHintProps) {
  return (
    <div className={cn('flex items-center gap-2 text-xs text-muted-foreground', className)}>
      <div className="flex items-center gap-1">
        {keys.map((key, index) => (
          <span key={index}>
            <kbd className="px-1.5 py-0.5 bg-muted border border-border rounded text-[10px] font-mono">
              {key}
            </kbd>
            {index < keys.length - 1 && <span className="mx-0.5">+</span>}
          </span>
        ))}
      </div>
      <span>{description}</span>
    </div>
  );
}

/**
 * Announce content to screen readers
 */
export function useAnnounce() {
  const announce = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    const announcer = document.createElement('div');
    announcer.setAttribute('aria-live', priority);
    announcer.setAttribute('aria-atomic', 'true');
    announcer.setAttribute('class', 'sr-only');
    announcer.textContent = message;
    document.body.appendChild(announcer);

    setTimeout(() => {
      document.body.removeChild(announcer);
    }, 1000);
  };

  return { announce };
}
